import { useEffect, useRef } from "react";
import { endsAfterMidnight, LATE_NIGHT_TAG } from "@/lib/timeOfDay";

/**
 * Keeps the "Late Night" tag in step with an event that runs past midnight.
 *
 * An organizer setting an end time of 1 AM has already told us the thing the
 * tag says, so asking them to tick it too is asking twice. The tag is added
 * for them, and taken back off if the end time moves earlier — but only while
 * it is still ours to move. The moment they tick or untick it themselves the
 * tag is theirs, and this stops touching it for the rest of the edit.
 */
export function useLateNightTag(
  startTime: string,
  endTime: string,
  tags: string[],
  setTags: (tags: string[]) => void,
) {
  /** True once the organizer has set the tag by hand, either way. */
  const organizerDecided = useRef(false);
  /** Whether this hook put the tag there, as opposed to it arriving with the event. */
  const weAddedIt = useRef(false);
  /** The tag as this hook last left it; any other value came from the organizer. */
  const expected = useRef(tags.includes(LATE_NIGHT_TAG));

  const late = endsAfterMidnight(startTime, endTime);
  const hasTag = tags.includes(LATE_NIGHT_TAG);

  if (hasTag !== expected.current) {
    organizerDecided.current = true;
    expected.current = hasTag;
  }

  useEffect(() => {
    if (organizerDecided.current) return;
    if (late && !hasTag) {
      weAddedIt.current = true;
      expected.current = true;
      setTags([...tags, LATE_NIGHT_TAG]);
    } else if (!late && hasTag && weAddedIt.current) {
      weAddedIt.current = false;
      expected.current = false;
      setTags(tags.filter((t) => t !== LATE_NIGHT_TAG));
    }
  }, [late, hasTag, tags, setTags]);
}
